/**
 * User Avatar Component
 * 
 * Displays a user's avatar image if available, otherwise falls back to initials.
 * Handles image loading errors gracefully.
 * Uses pastel colors for users without images.
 */

import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";

type UserAvatarProps = {
  imageUrl?: string | null;
  fallbackText: string; // e.g., "JD" or "john@example.com"
  title?: string; // Tooltip text
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
  className?: string;
};

const sizeClasses = {
  sm: "h-5 w-5 text-xs",
  md: "h-6 w-6 text-xs",
  lg: "h-8 w-8 text-sm",
  xl: "h-12 w-12 text-base md:h-10 md:w-10 md:text-sm",
  "2xl": "h-14 w-14 text-base md:h-12 md:w-12 md:text-sm",
};

// Pastel color palette - soft, pleasant colors
const pastelColors = [
  { bg: "bg-pink-100 dark:bg-pink-900/30", text: "text-pink-700 dark:text-pink-300" },
  { bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-300" },
  { bg: "bg-purple-100 dark:bg-purple-900/30", text: "text-purple-700 dark:text-purple-300" },
  { bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-300" },
  { bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-300" },
  { bg: "bg-indigo-100 dark:bg-indigo-900/30", text: "text-indigo-700 dark:text-indigo-300" },
  { bg: "bg-cyan-100 dark:bg-cyan-900/30", text: "text-cyan-700 dark:text-cyan-300" },
  { bg: "bg-rose-100 dark:bg-rose-900/30", text: "text-rose-700 dark:text-rose-300" },
  { bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-300" },
  { bg: "bg-teal-100 dark:bg-teal-900/30", text: "text-teal-700 dark:text-teal-300" },
  { bg: "bg-violet-100 dark:bg-violet-900/30", text: "text-violet-700 dark:text-violet-300" },
  { bg: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-700 dark:text-emerald-300" },
];

/**
 * Generate a deterministic pastel color based on a string
 */
function getPastelColor(text: string) {
  // Simple hash function to convert string to number
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash);
  }
  // Use absolute value and modulo to get index
  const index = Math.abs(hash) % pastelColors.length;
  return pastelColors[index];
}

export function UserAvatar({
  imageUrl,
  fallbackText,
  title,
  size = "md",
  className,
}: UserAvatarProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const sizeClass = sizeClasses[size];
  const initials = fallbackText
    .split(/[@.\s]/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("")
    .slice(0, 2) || "?";

  const showImage = imageUrl && !imageError;
  
  // Get pastel color for this user (deterministic based on fallbackText)
  const pastelColor = useMemo(() => getPastelColor(fallbackText), [fallbackText]);

  return (
    <div
      className={cn(
        "rounded-full border-[2.5px] border-background/90 flex items-center justify-center font-medium overflow-hidden backdrop-blur-sm",
        sizeClass,
        // Use pastel color when no image, otherwise use gradient
        showImage 
          ? "bg-gradient-to-br from-muted/50 to-muted/30"
          : pastelColor.bg,
        className
      )}
      title={title || fallbackText}
    >
      {showImage ? (
        <>
          {!imageLoaded && (
            <span className={cn("text-muted-foreground/70", pastelColor.text)}>{initials}</span>
          )}
          <img
            src={imageUrl}
            alt={title || fallbackText}
            className={cn(
              "w-full h-full object-cover transition-opacity duration-300",
              imageLoaded ? "opacity-100" : "opacity-0"
            )}
            onLoad={() => setImageLoaded(true)}
            onError={() => {
              setImageError(true);
              setImageLoaded(false);
            }}
          />
        </>
      ) : (
        <span className={pastelColor.text}>{initials}</span>
      )}
    </div>
  );
}

